import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from './api/axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Fetch complete user info when token is available
  const fetchUserInfo = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // If fetching user fails, logout
      logout();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Check if token is expired
        if (decoded.exp * 1000 < Date.now()) {
          logout();
          return;
        }
        
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        fetchUserInfo();
      } catch (error) {
        console.error('Invalid token:', error);
        logout();
      }
    } else {
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
  };

  const setupSuperAdmin = async (email, password, firstName, lastName) => {
    const res = await api.post('/auth/setup-super-admin', { 
      email, 
      password, 
      firstName, 
      lastName 
    });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const activateAccount = async (token, password) => {
    const res = await api.post('/auth/activate', { token, password });
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const inviteUser = async (invitationData) => {
    const res = await api.post('/auth/invite', invitationData);
    return res.data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
  };

  // Helper functions to check permissions
  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return user?.role && roles.includes(user.role);
  };

  const canManageUsers = () => {
    return hasAnyRole(['super_admin', 'admin']);
  };

  const canInviteUsers = () => {
    return hasAnyRole(['super_admin', 'admin', 'manager']);
  };

  const hasCompanyAccess = (companyId) => {
    if (hasAnyRole(['super_admin', 'admin'])) return true;
    return user?.assignedCompanies?.some(ac => ac.companyId === companyId);
  };

  const hasProjectAccess = (projectId) => {
    if (hasAnyRole(['super_admin', 'admin'])) return true;
    if (user?.role === 'manager') {
      // Managers have access to projects in their assigned companies
      return user?.assignedCompanies?.length > 0;
    }
    return user?.assignedProjects?.some(ap => ap.projectId === projectId);
  };

  const getProjectSectionAccess = (projectId) => {
    if (hasAnyRole(['super_admin', 'admin'])) {
      return {
        flowDesigner: true,
        execution: true,
        analytics: true,
        billing: true,
        settings: true,
        history: true
      };
    }
    
    if (user?.role === 'manager') {
      return {
        flowDesigner: true,
        execution: true,
        analytics: true,
        billing: false,
        settings: true,
        history: true
      };
    }

    const projectAssignment = user?.assignedProjects?.find(ap => ap.projectId === projectId);
    return projectAssignment?.sectionAccess || {
      flowDesigner: false,
      execution: true,
      analytics: false,
      billing: false,
      settings: false,
      history: true
    };
  };

  const value = {
    user,
    token,
    loading,
    login,
    setupSuperAdmin,
    activateAccount,
    inviteUser,
    logout,
    hasRole,
    hasAnyRole,
    canManageUsers,
    canInviteUsers,
    hasCompanyAccess,
    hasProjectAccess,
    getProjectSectionAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 